import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTrack } from './TrackLayout';
import { trackApi, errMsg } from '../../lib/trackApi';
import TaskFilterBar, { filtersToParams } from '../../components/track/TaskFilterBar';
import TaskCreateForm from '../../components/track/TaskCreateForm';
import { StatusSelect } from '../../components/track/StatusBadge';
import { formatFieldValue } from '../../components/track/DynamicField';
import TrackFieldDesigner from './TrackFieldDesigner';
import TrackBoardSettings from './TrackBoardSettings';
import {
  C, btn, btnGhost, btnSm, btnLink, todayYmd, fmtMD, fmtDateTime, dueInfo, flattenTree,
} from '../../components/track/trackUi';

const th = { padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap', fontSize: 12, color: C.muted, borderBottom: `2px solid ${C.border}` };
const td = { padding: '6px 8px', borderBottom: '1px solid #f3f4f6', fontSize: 13, verticalAlign: 'middle' };

export default function TrackBoard() {
  const { boardId: raw } = useParams();
  const boardId = Number(raw);
  const { boards, members, openTask, refreshKey, bumpRefresh, reloadMeta } = useTrack();
  const board = useMemo(() => boards.find((b) => b.id === boardId), [boards, boardId]);
  const [tab, setTab] = useState('tasks');
  const [fields, setFields] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({ boardId, assigneeId: null, initiatorId: null, status: '', q: '', starred: false, includeDone: false });
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [creating, setCreating] = useState(null);
  const [error, setError] = useState('');
  const today = todayYmd();

  const loadFields = useCallback(async () => {
    try {
      const d = await trackApi.listFields(boardId);
      setFields(d?.data || []);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [boardId]);

  const loadTasks = useCallback(async () => {
    try {
      const d = await trackApi.listTasks({ ...filtersToParams(filters), boardId });
      setTasks(d?.data || []);
      setError('');
    } catch (e) {
      setError(errMsg(e));
    }
  }, [filters, boardId]);

  useEffect(() => { loadFields(); }, [loadFields]);
  useEffect(() => {
    const t = setTimeout(loadTasks, 250);
    return () => clearTimeout(t);
  }, [loadTasks, refreshKey]);

  const listFields = fields.filter((f) => f.showInList);
  const rows = useMemo(() => flattenTree(tasks, collapsed), [tasks, collapsed]);

  const toggleCollapse = (id) => setCollapsed((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const patch = async (task, body) => {
    try {
      await trackApi.patchTask(task.id, body);
      loadTasks();
      bumpRefresh();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  if (!board) {
    return <div style={{ color: C.muted }}>找不到看板。<Link to="..">返回</Link></div>;
  }

  const tabBtn = (key, label) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      style={{ ...(tab === key ? btn : btnGhost), marginRight: 6 }}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <Link to=".." relative="path" style={{ color: C.muted, fontSize: 13 }}>← 看板</Link>
        <h2 style={{ margin: 0, fontSize: 20 }}>{board.name}</h2>
        {board.description && <span style={{ color: C.muted, fontSize: 13 }}>{board.description}</span>}
      </div>
      <div style={{ marginBottom: 12 }}>
        {tabBtn('tasks', '任務')}
        {tabBtn('fields', '表單欄位')}
        {tabBtn('settings', '設定與通知')}
      </div>
      {error && <div style={{ color: C.danger, marginBottom: 8 }}>{error}</div>}

      {tab === 'tasks' && (
        <>
          <div style={{ marginBottom: 10 }}>
            <TaskFilterBar boards={boards} members={members} value={filters} onChange={(v) => setFilters({ ...v, boardId })} show={{ board: false }}>
              <button type="button" style={{ ...btn, marginLeft: 'auto' }} onClick={() => setCreating(creating === 'root' ? null : 'root')}>
                {creating === 'root' ? '收合' : '＋ 新增任務'}
              </button>
            </TaskFilterBar>
          </div>
          {creating === 'root' && (
            <div style={{ marginBottom: 12, maxWidth: 720 }}>
              <TaskCreateForm
                boards={boards}
                members={members}
                boardId={boardId}
                onCreated={() => { setCreating(null); loadTasks(); bumpRefresh(); }}
                onCancel={() => setCreating(null)}
              />
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={th}>編號</th>
                  <th style={th}>任務</th>
                  <th style={th}>發起人</th>
                  <th style={th}>執行人</th>
                  <th style={th}>狀態</th>
                  <th style={th}>開始</th>
                  <th style={th}>預計完成</th>
                  {listFields.map((f) => <th key={f.id} style={th}>{f.label}</th>)}
                  <th style={th}>最新備註</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ task: t, depth, hasChildren }) => {
                  const due = dueInfo(t, today, board);
                  return (
                    <React.Fragment key={t.id}>
                      <tr style={{ background: t.isStarred ? '#fffdf5' : undefined }}>
                        <td style={{ ...td, color: C.muted, whiteSpace: 'nowrap' }}>{t.taskNo}</td>
                        <td style={{ ...td, paddingLeft: 8 + depth * 22, minWidth: 220 }}>
                          {hasChildren ? (
                            <button type="button" style={{ ...btnLink, color: C.muted, width: 16 }} onClick={() => toggleCollapse(t.id)}>
                              {collapsed.has(t.id) ? '▸' : '▾'}
                            </button>
                          ) : <span style={{ display: 'inline-block', width: 16 }}>{depth ? '└' : ''}</span>}
                          <button
                            type="button"
                            onClick={() => patch(t, { isStarred: !t.isStarred })}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: t.isStarred ? C.star : '#d1d5db', padding: '0 4px' }}
                          >
                            {t.isStarred ? '★' : '☆'}
                          </button>
                          <button type="button" style={{ ...btnLink, fontSize: 13, color: '#111', fontWeight: depth ? 500 : 700 }} onClick={() => openTask(t.id)}>
                            {t.title}
                          </button>
                          {t.childCount > 0 && <span style={{ fontSize: 11, color: C.muted }}> {t.childDone}/{t.childCount}</span>}
                          {t.attachCount > 0 && <span style={{ fontSize: 11, color: C.muted }}>（附件 {t.attachCount}）</span>}
                        </td>
                        <td style={td}>{t.initiatorName}</td>
                        <td style={td}>{t.assigneeName || <span style={{ color: '#9ca3af' }}>未指派</span>}</td>
                        <td style={td}><StatusSelect statuses={board.statuses} value={t.status} onChange={(v) => patch(t, { status: v })} /></td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtMD(t.startDate)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', color: due.color }}>{due.text}</td>
                        {listFields.map((f) => <td key={f.id} style={td}>{formatFieldValue(f, t.extra?.[f.key], members)}</td>)}
                        <td style={{ ...td, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.lastNote}>
                          {t.lastNote && <span style={{ color: C.muted, fontSize: 11 }}>{fmtDateTime(t.lastNoteAt)} </span>}
                          {t.lastNote}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => setCreating(creating === t.id ? null : t.id)}>＋子任務</button>
                        </td>
                      </tr>
                      {creating === t.id && (
                        <tr>
                          <td colSpan={9 + listFields.length} style={{ padding: 8 }}>
                            <div style={{ maxWidth: 720 }}>
                              <TaskCreateForm
                                boards={boards}
                                members={members}
                                parent={t}
                                onCreated={() => { setCreating(null); loadTasks(); bumpRefresh(); }}
                                onCancel={() => setCreating(null)}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {!rows.length && <div style={{ color: C.muted, padding: '16px 0' }}>沒有任務</div>}
          </div>
        </>
      )}

      {tab === 'fields' && <TrackFieldDesigner boardId={boardId} fields={fields} members={members} onChanged={loadFields} />}
      {tab === 'settings' && <TrackBoardSettings board={board} onSaved={reloadMeta} />}
    </div>
  );
}
